import { Map } from 'ol';
import { Group } from 'ol/layer';
import ImageLayer from 'ol/layer/Image';
import { useCallback, useMemo } from 'react';
import { ImageStatic } from 'ol/source';
import { ImageSourceEvent } from 'ol/source/Image';
import { Coordinate } from 'ol/coordinate';
import type { MessageInstance } from 'antd/es/message/interface';
import { Dayjs } from 'dayjs';
import { Product } from 'types/app.types';
import { WMSCapabilities } from 'utils/types/WMSCapabilities.interface';
import { getFeatureInfo } from 'services/geoserver/outboundsRequest';
import { featureInfoCache, purgeLayerImages } from 'utils/imageCaches';
import {
  createImageSource, getLayerByName, recreateSource, getFeatureInfoUrl,
} from '../utils';
import LayerName from '../enums';
import zIndexes from '../../../../configs/zIndexes';
import { utcDayjs } from '../../../../utils';
import { LayerManagementAdapter, PopupLayerEntry } from './types';

interface ImageSourceEventAndTime extends ImageSourceEvent { time?: string }

function updateGroupLayerOrder(group: Group) {
  const layers = group.getLayers().getArray().sort((a, b) => a.get('layerIndex') - b.get('layerIndex'));
  const zIndex = layers.length;
  layers.forEach((layer, i) => {
    layer.setZIndex(zIndexes.GEOLAYERS + zIndex - i);
  });
}

export interface GeoServerAdapterDeps {
  map: Map;
  capabilities: WMSCapabilities | undefined;
  currentProjection: string;
  message: MessageInstance;
  editProductTime: (index: number, time: Dayjs | undefined) => void;
  setLoadingLayer: (loading: boolean) => void;
  preloadLayerFn: (params: {
    index: number;
    product: Product;
    generatedTimestampList: string[] }) => Promise<void>;
}

export function useGeoServerAdapter(deps: GeoServerAdapterDeps): LayerManagementAdapter {
  const {
    map, capabilities, currentProjection, message, editProductTime, setLoadingLayer,
    preloadLayerFn,
  } = deps;

  const addLayer = useCallback(async (product: Product, time: string) => {
    if (!capabilities) return;
    const layersInCapabilities = capabilities.Capability.Layer.Layer;
    const layerInCapabilities = layersInCapabilities.find((l) => l.Name === product?.internalName);
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (!geoLayer || !layerInCapabilities) return;

    const boundingBox = layerInCapabilities.BoundingBox;
    const { crs } = boundingBox?.find((bbox) => bbox.crs.includes('EPSG')) || {};
    try {
      const source = createImageSource({
        layers: product.name,
        styles: product.currentStyle,
        extent: boundingBox,
        projection: crs || 'EPSG:3857',
        elevations: product.currentElevation,
        currentProjection,
        time,
        onError: () => {
          setLoadingLayer(false);
          message.error(`An error occurred while downloading layer '${product.name}'`);
        },
      });
      source.on('imageloadend', (event: ImageSourceEventAndTime) => {
        if (event.time) {
          editProductTime(product.index, event.time !== 'outOfRange' ? utcDayjs(event.time) : undefined);
        }
      });

      const newLayer = new ImageLayer({
        opacity: (product.currentOpacity || 80) / 100,
        source,
      });
      newLayer.set('name', product.name);
      newLayer.set('layerIndex', product.index);
      newLayer.set('id', product.id);
      newLayer.set('internalName', product.internalName);
      newLayer.set('unit', product.layer.unit);
      geoLayer.getLayers().push(newLayer);
      updateGroupLayerOrder(geoLayer);
    } catch (error) {
      setLoadingLayer(false);
      message.error(`An error occurred while downloading layer '${product.name}'. ${error}`);
    }
  }, [capabilities, currentProjection, editProductTime, map, message, setLoadingLayer]);

  const replaceLayer = useCallback((product: Product) => {
    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    const layersSnapshot = [...geoLayer.getLayers().getArray()] as ImageLayer<ImageStatic>[];
    layersSnapshot.forEach((layer) => {
      if (layer.get('layerIndex') === product.index) {
        // Replaced layer/style/elevation: frames preloaded for the old one are unreachable.
        purgeLayerImages(layer);
        layer.set('name', product.name);
        layer.set('layerIndex', product.index);
        layer.set('id', product.id);
        layer.set('internalName', product.internalName);
        layer.set('unit', product.layer.unit);
        try {
          const source = recreateSource({
            layer,
            params: {
              styles: product.currentStyle,
              layers: product.name,
              elevation: product.currentElevation?.toString(),
            },
            currentProjection,
          });
          layer.setSource(source);
        } catch (error) {
          message.error(`An error occurred while updating layer. ${error}`);
        }
      }
    });
  }, [currentProjection, map, message]);

  // GeoServer layer time updates are driven by useTimeSeriesPlayer's recreateSource effect.
  const updateLayerTime = useCallback(async (_productIndex: number, _isoTime: string) => {}, []);

  // GeoServer style updates are handled by useProductLayers via updateParam.
  const updateLayerStyle = useCallback((
    _productIndex: number,
    _styleName: string,
    _filterIds?: string[],
  ) => {}, []);

  const notifyRemove = useCallback((_productId: string) => { /* no-op for GeoServer */ }, []);

  const preloadLayer = useCallback(
    (params: {
      index: number;
      product: Product;
      generatedTimestampList: string[];
    }) => preloadLayerFn(params),
    [preloadLayerFn],
  );

  const getLayerTooltip = useCallback(async (
    layer: Parameters<LayerManagementAdapter['getLayerTooltip']>[0],
    coordinate: Coordinate | number[],
    _destinationProjection: string,
    mapInstance: Map,
  ): Promise<PopupLayerEntry[]> => {
    const source = layer.getSource();
    if (!(source instanceof ImageStatic)) return [];
    const url = getFeatureInfoUrl({
      resolution: mapInstance.getView().getResolution() || 1,
      coordinate,
      params: {
        layers: source.get('layers'),
        styles: source.get('styles'),
        projection: source.get('projection'),
        time: source.get('time'),
        elevations: source.get('elevations'),
      },
    });
    if (!url) return [];
    try {
      let value: { value: number; unit: string }[] | undefined;
      const cachedData = featureInfoCache.get(url);
      if (cachedData) {
        value = JSON.parse(cachedData);
      } else {
        value = await getFeatureInfo(url);
        featureInfoCache.set(url, JSON.stringify(value));
      }
      if (!value) return [];
      return source.get('layers').split(',').map((layerName: string, i: number): PopupLayerEntry => ({
        label: layerName,
        value: value?.[i]?.value ?? null,
        unit: layer.get('unit') || '',
        style: source.get('styles').split(',')[i] || '',
        internalName: layer.get('internalName') as string,
        provider: 'geoserver',
      }));
    } catch {
      return [];
    }
  }, []);

  return useMemo(() => ({
    playbackTooltipEnabled: false as const,
    addLayer,
    replaceLayer,
    updateLayerTime,
    notifyRemove,
    preloadLayer,
    getLayerTooltip,
    updateLayerStyle,
  }), [addLayer,
    replaceLayer,
    updateLayerTime, notifyRemove, preloadLayer, getLayerTooltip, updateLayerStyle]);
}
